import create from 'zustand';

import {
  AssetPermission,
  AvailableBankAccountPermissionItem,
  AvailableCorporateCardPermissionItem,
  SecurityAccountPermissionInfo,
  CompanyUserPermission,
  UpdateRoleData,
} from '~apis/role/type';

type EditRoleDetailData = UpdateRoleData & {
  isSystemRole: boolean;
  isChanged?: boolean;
};

interface RolePermissionStore {
  editRoleDetailData?: EditRoleDetailData;
  setEditRoleDetailData: (data?: EditRoleDetailData) => void;
  updateRoleName: (roleName: string) => void;
  updateCompanyUserPermissions: (list: CompanyUserPermission[]) => void;
  updateTaxInvoicePermission: (allowAllEdit: boolean) => void;
  updateCashReceiptPermission: (allowAllEdit: boolean) => void;
  updateBankAccountAllView: (allowAllView: boolean) => void;
  updateBankAccountAllEdit: (allowAllEdit: boolean) => void;
  updateCardAllView: (allowAllView: boolean) => void;
  updateCardAllEdit: (allowAllEdit: boolean) => void;
  updateBankAccountItem: (
    item: AvailableBankAccountPermissionItem | SecurityAccountPermissionInfo,
    relationName: AssetPermission,
    checked: boolean,
  ) => void;
  updateBankAccountAll: (
    data: {
      objectId: string;
      relationName: AssetPermission;
    }[],
  ) => void;
  updateCardAll: (
    data: {
      objectId: string;
      relationName: AssetPermission;
    }[],
  ) => void;
  updateCardItem: (
    item: AvailableCorporateCardPermissionItem,
    relationName: AssetPermission,
    checked: boolean,
  ) => void;
  updateSecurityAccountAllView: (allowAllView: boolean) => void;
  updateSecurityAccountAllEdit: (allowAllEdit: boolean) => void;
  updateSecurityAccountAll: (
    data: {
      objectId: string;
      relationName: AssetPermission;
    }[],
  ) => void;
  addMember: (userId: string) => void;
  removeMember: (userId: string) => void;
  resetStore: () => void;
}

const initialState = {
  editRoleDetailData: undefined,
};

const useRolePermissionStore = create<RolePermissionStore>((set, get) => ({
  ...initialState,

  setEditRoleDetailData: (data) => set(() => ({ editRoleDetailData: data })),

  updateRoleName: (roleName) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          name: roleName,
          isChanged: true,
        },
      };
    }),

  updateCompanyUserPermissions: (list) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          companyUserPermissions: list,
          isChanged: true,
        },
      };
    }),

  updateTaxInvoicePermission: (allowAllEdit) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          taxInvoicePermissions: {
            ...state.editRoleDetailData.taxInvoicePermissions,
            allowAllView: false,
            allowAllEdit,
          },
          isChanged: true,
        },
      };
    }),

  updateCashReceiptPermission: (allowAllEdit) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          cashReceiptPermissions: {
            ...state.editRoleDetailData.cashReceiptPermissions,
            allowAllView: false,
            allowAllEdit,
          },
          isChanged: true,
        },
      };
    }),

  updateBankAccountAllView: (allowAllView) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          bankAccountPermissions: {
            ...state.editRoleDetailData.bankAccountPermissions,
            allowAllView,
          },
          isChanged: true,
        },
      };
    }),

  updateBankAccountAllEdit: (allowAllEdit) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          bankAccountPermissions: {
            ...state.editRoleDetailData.bankAccountPermissions,
            allowAllEdit,
          },
          isChanged: true,
        },
      };
    }),

  updateCardAllView: (allowAllView) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          corporateCardPermissions: {
            ...state.editRoleDetailData.corporateCardPermissions,
            allowAllView,
          },
          isChanged: true,
        },
      };
    }),

  updateCardAllEdit: (allowAllEdit) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          corporateCardPermissions: {
            ...state.editRoleDetailData.corporateCardPermissions,
            allowAllEdit,
          },
          isChanged: true,
        },
      };
    }),

  updateBankAccountItem: (item, relationName, checked) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;

      // organizationType으로 증권계좌인지 확인
      const isSecurityAccount = 'organizationType' in item && item.organizationType === 'SECURITY';

      if (isSecurityAccount) {
        // 증권계좌인 경우 securityAccountPermissions 업데이트
        return {
          editRoleDetailData: {
            ...state.editRoleDetailData,
            securityAccountPermissions: {
              ...state.editRoleDetailData.securityAccountPermissions,
              permissions: checked
                ? [
                    ...state.editRoleDetailData.securityAccountPermissions.permissions.filter(
                      (permission) => permission.objectId !== item.objectId,
                    ),
                    {
                      objectId: item.objectId,
                      relationName,
                    },
                  ]
                : state.editRoleDetailData.securityAccountPermissions.permissions.filter(
                    (permission) => permission.objectId !== item.objectId,
                  ),
            },
            isChanged: true,
          },
        };
      } else {
        // 은행계좌인 경우 bankAccountPermissions 업데이트
        return {
          editRoleDetailData: {
            ...state.editRoleDetailData,
            bankAccountPermissions: {
              ...state.editRoleDetailData.bankAccountPermissions,
              permissions: checked
                ? [
                    ...state.editRoleDetailData.bankAccountPermissions.permissions.filter(
                      (permission) => permission.objectId !== item.objectId,
                    ),
                    {
                      objectId: item.objectId,
                      relationName,
                    },
                  ]
                : state.editRoleDetailData.bankAccountPermissions.permissions.filter(
                    (permission) => permission.objectId !== item.objectId,
                  ),
            },
            isChanged: true,
          },
        };
      }
    }),

  updateBankAccountAll: (data) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;

      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          bankAccountPermissions: {
            ...state.editRoleDetailData.bankAccountPermissions,
            permissions: data,
          },
          isChanged: true,
        },
      };
    }),

  updateCardAll: (data) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          corporateCardPermissions: {
            ...state.editRoleDetailData.corporateCardPermissions,
            permissions: data,
          },
          isChanged: true,
        },
      };
    }),

  updateCardItem: (item, relationName, checked) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;

      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          corporateCardPermissions: {
            ...state.editRoleDetailData.corporateCardPermissions,
            permissions: checked
              ? [
                  ...state.editRoleDetailData.corporateCardPermissions.permissions.filter(
                    (permission) => permission.objectId !== item.objectId,
                  ),
                  {
                    objectId: item.objectId,
                    relationName,
                  },
                ]
              : state.editRoleDetailData.corporateCardPermissions.permissions.filter(
                  (permission) => permission.objectId !== item.objectId,
                ),
          },
          isChanged: true,
        },
      };
    }),

  updateSecurityAccountAllView: (allowAllView) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          securityAccountPermissions: {
            ...state.editRoleDetailData.securityAccountPermissions,
            allowAllView,
          },
          isChanged: true,
        },
      };
    }),

  updateSecurityAccountAllEdit: (allowAllEdit) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          securityAccountPermissions: {
            ...state.editRoleDetailData.securityAccountPermissions,
            allowAllEdit,
          },
          isChanged: true,
        },
      };
    }),

  updateSecurityAccountAll: (
    data: {
      objectId: string;
      relationName: AssetPermission;
    }[],
  ) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          securityAccountPermissions: {
            ...state.editRoleDetailData.securityAccountPermissions,
            permissions: data,
          },
          isChanged: true,
        },
      };
    }),

  addMember: (userId) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          assignedUsers: [...(state.editRoleDetailData?.assignedUsers ?? []), { userId }],
          isChanged: true,
        },
      };
    }),

  removeMember: (userId) =>
    set((state) => {
      if (!state.editRoleDetailData) return state;
      return {
        editRoleDetailData: {
          ...state.editRoleDetailData,
          assignedUsers: state.editRoleDetailData.assignedUsers.filter(
            (user) => user.userId !== userId,
          ),
          isChanged: true,
        },
      };
    }),

  resetStore: () => set(() => initialState),
}));

export default useRolePermissionStore;
